import React, { KeyboardEventHandler, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import useStyles from 'isomorphic-style-loader/useStyles';

import {
	Button, Input, MultipleSelect, Textarea,
} from '@sololearnorg/sol';
import { Container } from '../../../../symphony';

import { useContainerData } from '../../../../shared/public/SlHooks/useContainerData';
import { SlDiscussContext } from '../../global-constants';
import { SlActions } from '../../services/ActionsService/sl-actions';
import {
	ActionType,
	ISelectedTags,
	QuestionPageType,
	RequestType,
} from '../../global-interface';
import { SlDataService } from '../../services/sl-data.service';
import { useLeaveQuestion } from './useLeaveQuestion';

import s from './CreateEditQuestion.scss';
import { useConfirmLeave } from '../../../../shared/public/SlHooks/useConfirmLeave';

const schema = yup
	.object({
		title: yup.string().required('Please enter title'),
		description: yup.string().required(),
	})
	.required();

const createOption = (label: string) => ({
	label,
	value: label,
});

const MAX_TAGS_COUNT = 10;

const MAX_TAG_INPUT = 64;

const TAGS_CAPTION = `You can add up to ${MAX_TAGS_COUNT} tags`;

interface ICreateEditQuestionProsp {
	type: QuestionPageType;
}

export function CreateEditQuestion({ type }: ICreateEditQuestionProsp) {
	const { actions$ } = Container.take(SlDiscussContext, SlActions);
	useStyles(s);

	const {
		suggestedTags$: suggestedTags,
		postQuestionLoading$: postQuestionLoading,
		question$: question,
	} = useContainerData(SlDiscussContext, SlDataService, [
		'suggestedTags$',
		'postQuestionLoading$',
		'question$',
	]);

	const [selectedTags, setSelectedTags] = useState<ISelectedTags[]>([]);
	const [tagInputValue, setTagInputValue] = useState<string>('');
	const [isOpenTagMenu, setIsOpenTagMenu] = useState<boolean>(undefined);
	const [showMaxTagsMessage, setShowMaxTagsMessage] = useState<boolean>(false);

	const {
		handleSubmit,
		formState: { isValid },
		register,
		getValues,
	} = useForm({
		resolver: yupResolver(schema),
		values: {
			title: question?.title,
			description: question?.message,
		},
	});

	const { alias: id } = useParams();

	useLeaveQuestion({
		questionId: question?.id,
		title: getValues('title'),
	});

	useEffect(() => {
		if (question) {
			setSelectedTags(question.tags.map(createOption));
		}
	}, [question]);

	useEffect(() => {
		actions$.next({
			type: ActionType.OnCreateEditQuestionMount,
			requestType: RequestType.Parallel,
			data: {
				questionPageType: type,
				questionId: id,
			},
		});

		return () => {
			actions$.next({
				type: ActionType.OnCreateEditQuestionUnMount,
				requestType: RequestType.Parallel,
			});
		};
	}, []);

	const postQuestion = (values) => {
		actions$.next({
			type: ActionType.OnQuestionSubmit,
			requestType: RequestType.Parallel,
			data: {
				postQuestionPayload: {
					tags: selectedTags.map((el) => el.value),
					title: values.title,
					message: values.description,
				},
				questionPageType: type,
			},
		});
	};

	const handleTagsChange = (value) => {
		setIsOpenTagMenu(false);
		setSelectedTags(value);
	};

	const handleCancelClick = () => {
		actions$.next({
			type: ActionType.OnQuestionPostCancelClick,
			requestType: RequestType.Parallel,
			data: {
				questionPageType: type,
			},
		});
	};

	const handleDeleteClick = () => {
		actions$.next({
			type: ActionType.OnQuestionDeleteClick,
			requestType: RequestType.Parallel,
		});
	};

	const handleTagInputChange = (value: string) => {
		if (selectedTags.length >= MAX_TAGS_COUNT) {
			setIsOpenTagMenu(undefined);
			setShowMaxTagsMessage(true);
			return;
		}
		setShowMaxTagsMessage(false);

		const modifiedValue = value.trim();

		if (modifiedValue.length <= MAX_TAG_INPUT) {
			setTagInputValue(modifiedValue);
		}

		if (modifiedValue) {
			setIsOpenTagMenu(undefined);
			actions$.next({
				type: ActionType.OnPostTagInputChange,
				requestType: RequestType.Parallel,
				data: {
					tagInput: modifiedValue,
				},
			});
		}
	};

	const handleKeyDown: KeyboardEventHandler = (event) => {
		if (!tagInputValue || selectedTags.length >= MAX_TAGS_COUNT) return;

		switch (event.code) {
		case 'Enter':
		case 'Space':
			if (selectedTags.some((el) => el.value === tagInputValue)) {
				break;
			}

			handleTagsChange([
				...selectedTags,
				createOption(tagInputValue),
			]);
			setTagInputValue('');
			event.preventDefault();
			break;
		default:
			break;
		}
	};

	const handleOptionsMessage = () => {
		if (showMaxTagsMessage) {
			return (
				<p className="discuss-new-question__max-tags-text">
					You&apos;ve used the maximum of 10 tags. To add others,
					remove one or more
				</p>
			);
		}
		return <p>No options</p>;
	};

	useConfirmLeave(true);

	if (type === QuestionPageType.Edit && !question) {
		return null;
	}

	return (
		<div className="discuss-new-question">
			{type === QuestionPageType.New && (
				<h3 className="discuss-new-question__title">
					Ask the community a question
				</h3>
			)}
			<form
				className="discuss-new-question__form"
				onSubmit={handleSubmit(postQuestion)}
			>
				<Input
					size="m"
					type="text"
					name="title"
					placeholder="What would you like to know?"
					label="Your question"
					className="discuss-new-question__input"
					maxLength={128}
					{...register('title')}
					caption="Tip: write as if asking a friend, being as specific as possible"
				/>
				<Textarea
					size="m"
					type="description"
					className="discuss-new-question__textarea"
					name="Description"
					{...register('description')}
					placeholder="Include as much detail as possible to get the most relevant answers."
					maxLength={2048}
					label="Description"
				/>
				<MultipleSelect
					size="m"
					label="Tags"
					className="discuss-new-question__tags-input"
					name="tags"
					onInputChange={handleTagInputChange}
					onValueChange={handleTagsChange}
					inputValue={tagInputValue}
					options={suggestedTags}
					open={isOpenTagMenu}
					onKeyDown={handleKeyDown}
					placeholder="Start typing to add tags…"
					listText={showMaxTagsMessage ? null : undefined}
					noOptionsMessage={handleOptionsMessage}
					isOptionDisabled={() => selectedTags.length >= MAX_TAGS_COUNT}
					value={selectedTags}
					caption={TAGS_CAPTION}
				/>
				<div
					className={`discuss-new-question__buttons${type === QuestionPageType.Edit ? ' edit' : ''
					}`}
				>
					{type === QuestionPageType.Edit && (
						<Button
							buttonType="secondary"
							className="discuss-new-question__buttons__delete"
							onClick={handleDeleteClick}
						>
							Delete question
						</Button>
					)}
					<Button buttonType="secondary" onClick={handleCancelClick}>
						Cancel
					</Button>
					{type === QuestionPageType.New && (
						<Button
							type="submit"
							disabled={!isValid || !selectedTags.length}
							loading={postQuestionLoading}
						>
							Post question
						</Button>
					)}
					{type === QuestionPageType.Edit && (
						<Button
							type="submit"
							disabled={!isValid || !selectedTags.length}
							loading={postQuestionLoading}
						>
							Save changes
						</Button>
					)}
				</div>
			</form>
		</div>
	);
}
